<template>
    <base-demo-flow
        :phone_key="phone_key"
        :phone_mode="phone_mode"
        :browser_content_key="current_step"
        business_name="Barrel Creek Vineyards"
        phone_number="(555) 123-4567"
        browser_url="barrelcreek.com/shop/2024-sauvignon-blanc"
        phone_label="Sarah's Phone"
        :current_step_number="current_step_number"
        :total_steps="total_steps"
        :progress_percentage="progress_percentage"
        :current_step_name="current_step_name"
        :can_go_previous="can_go_previous"
        :can_go_next="can_go_next"
        :steps="formatted_steps"
        @restart="resetDemo"
        @previous="goToPreviousStep"
        @next="goToNextStep"
        @step-click="goToStep"
    >
        <template #explainer>
            <component
                :is="explainer_component"
                :customer_data="customer_data"
                :wine_data="wine_data"
                @reset="resetDemo"
            />
        </template>

        <template #phone-messages>
            <mock-text-conversation
                :messages="conversation"
                :previously_shown_count="previously_shown_message_count"
            />
        </template>

        <template #phone-browser>
            <mock-wine-release-landing
                v-if="current_step === 'landing-page'"
                :wine_data="wine_data"
                :customer_data="customer_data"
                @reserve="handleReserve"
            />
        </template>
    </base-demo-flow>
</template>

<script>
import BaseDemoFlow from '@/components/demos/shared/base-demo-flow.vue';
import DemoStepManager from '@/mixins/demo-step-manager.js';
import NoIndexPage from '@/mixins/noindex-page.js';
import ExplainerInitialMessage from '@/components/demos/indirect-alcohol-promo/explainer-initial-message.vue';
import ExplainerLandingPage from '@/components/demos/indirect-alcohol-promo/explainer-landing-page.vue';
import ExplainerCustomerResponse from '@/components/demos/indirect-alcohol-promo/explainer-customer-response.vue';
import MockTextConversation from '@/components/demos/shared/mock-text-conversation.vue';
import MockWineReleaseLanding from '@/components/demos/indirect-alcohol-promo/mock-wine-release-landing.vue';

export default {
    name: 'DemoIndirectAlcoholPromoFlow',

    mixins: [DemoStepManager, NoIndexPage],

    components: {
        BaseDemoFlow,
        ExplainerInitialMessage,
        ExplainerLandingPage,
        ExplainerCustomerResponse,
        MockTextConversation,
        MockWineReleaseLanding
    },

    data() {
        return {
            reservation_data: {
                quantity: 6,
                message: "I'll take 6!"
            },
            customer_data: {
                first_name: 'Sarah',
                last_name: 'Johnson',
                phone: '(555) 987-6543',
                member_since: '2021',
                club_tier: 'Reserve Club'
            },
            wine_data: {
                name: '2024 Sauvignon Blanc',
                varietal: 'Sauvignon Blanc',
                vintage: '2024',
                regular_price: 45.99,
                club_price: 39.99,
                max_allocation: 6,
                tasting_notes: 'Bright citrus and tropical fruit notes with a crisp, refreshing finish.',
                release_type: 'Member Exclusive'
            }
        }
    },

    computed: {
        explainer_component() {
            switch (this.current_step) {
                case 'initial-text':
                    return 'ExplainerInitialMessage';
                case 'landing-page':
                    return 'ExplainerLandingPage';
                case 'customer-response':
                    return 'ExplainerCustomerResponse';
                default:
                    return null;
            }
        },

        initial_message_text() {
            return `Hi ${this.customer_data.first_name}, this is the team at Barrel Creek Vineyards!\n\nWe have an exciting new release to share with you: {link}\n\nQuantities are limited and reserved for members only. If interested, please let us know asap and we'll hold them for you!\n\nText STOP to opt-out.`;
        },

        short_link() {
            return {
                url: '#',
                text: 'smsly.io/Bc7Kx',
                preventDefault: true,
                callback: this.handleGoToLandingPage
            };
        },

        conversation() {
            const messages = [
                {
                    type: 'customer',
                    text: this.initial_message_text,
                    link: this.short_link,
                    time: '10:15 AM'
                }
            ];

            if (this.current_step === 'customer-response') {
                messages.push({
                    type: 'company',
                    text: this.reservation_data.message,
                    time: '10:23 AM'
                });

                const response_text = this.reservation_data.quantity
                    ? this.buildConfirmationMessage(this.reservation_data.quantity)
                    : `Thanks for your message, ${this.customer_data.first_name}! A team member will follow up with you shortly. We appreciate you being a valued ${this.customer_data.club_tier} member!`;

                messages.push({
                    type: 'customer',
                    text: response_text,
                    time: '10:24 AM'
                });
            }

            return messages;
        }
    },

    methods: {
        buildConfirmationMessage(quantity) {
            const total = (quantity * this.wine_data.club_price).toFixed(2);
            return `You got it, ${this.customer_data.first_name}! We've reserved ${quantity} for you.\n\nTotal: $${total} (plus tax & shipping)\n\nWe'll send you a link to complete your purchase when it's ready to ship. Thanks for being a valued ${this.customer_data.club_tier} member!`;
        },

        getStepConfig() {
            return {
                initial_step: 'initial-text',
                initial_phone_mode: 'messages',
                steps: ['initial-text', 'landing-page', 'customer-response'],
                step_names: {
                    'initial-text': 'SMS Announcement',
                    'landing-page': 'Wine Release Page',
                    'customer-response': 'Customer Reply'
                },
                step_definitions: {
                    'initial-text': {
                        phone_mode: 'messages'
                    },
                    'landing-page': {
                        phone_mode: 'browser'
                    },
                    'customer-response': {
                        phone_mode: 'messages'
                    },
                }
            };
        },

        handleGoToLandingPage() {
            this.navigateToStep('landing-page');
        },

        handleReserve(data) {
            this.reservation_data = data;
            this.navigateToStep('customer-response');
        }
    },

    mounted() {
        document.title = "Indirect Alcohol Promotion Demo | RedChirp";
        this.initializeSteps(this.getStepConfig());
    }
}
</script>

<style lang="scss" scoped></style>
